import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Check, X, MessageSquare, ChevronDown, ChevronUp } from 'lucide-react';

const API = '/api';

const TIER_COLORS = {
  'Tier 1': 'bg-emerald-600 text-white',
  'Tier 2': 'bg-blue-500 text-white',
  'Tier 3': 'bg-amber-500 text-white',
  'Tier 4': 'bg-purple-500 text-white',
  'Tier 5': 'bg-slate-400 text-white',
};

const FIT_COLORS = {
  'Strategic': 'bg-emerald-100 text-emerald-700',
  'Adjacent': 'bg-blue-100 text-blue-700',
  'Opportunistic': 'bg-amber-100 text-amber-700',
  'Weak': 'bg-slate-100 text-slate-600',
};

export default function SharedMappingPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedComment, setExpandedComment] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/public/shared-mapping/${token}`);
        setData(res.data);
      } catch {
        setError('This mapping link is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const handleApproval = async (companyName, status) => {
    setSubmitting(companyName);
    try {
      await axios.post(`${API}/public/shared-mapping/${token}/approve`, {
        company_name: companyName,
        status,
        comment: expandedComment === companyName ? commentText : '',
      });
      // Refresh data
      const res = await axios.get(`${API}/public/shared-mapping/${token}`);
      setData(res.data);
      setExpandedComment(null);
      setCommentText('');
    } catch { /* silent */ }
    finally { setSubmitting(null); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center px-6">
          <p className="text-sm text-red-500 mb-2">{error}</p>
          <p className="text-xs text-slate-400">Please contact the person who shared this link.</p>
        </div>
      </div>
    );
  }

  const { asset_name, biotech_name, therapeutic_area, development_stage, target_companies, summary, shared_by } = data;
  const tierOrder = { 'Tier 1': 0, 'Tier 2': 1, 'Tier 3': 2, 'Tier 4': 3, 'Tier 5': 4 };
  const sorted = [...(target_companies || [])].sort((a, b) => {
    const at = tierOrder[a.priority_tier] ?? 5;
    const bt = tierOrder[b.priority_tier] ?? 5;
    if (at !== bt) return at - bt;
    return (b.fit_score || 0) - (a.fit_score || 0);
  });

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: "'Public Sans', sans-serif" }}>
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-lg font-bold text-slate-900" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Target Mapping Review
              </h1>
              <p className="text-xs text-slate-500 mt-0.5">
                {asset_name} {therapeutic_area ? `- ${therapeutic_area}` : ''} {development_stage ? `(${development_stage})` : ''}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-slate-400">Shared by {shared_by || 'ForesightRx'}</p>
              {summary && (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[10px] font-semibold text-emerald-600">{summary.approved} Approved</span>
                  <span className="text-[10px] font-semibold text-red-500">{summary.rejected} Rejected</span>
                  <span className="text-[10px] font-semibold text-slate-400">{summary.pending} Pending</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Target List */}
      <div className="max-w-5xl mx-auto px-6 py-6">
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {sorted.map((tc, idx) => {
            const isExpanded = expandedComment === tc.company_name;
            const approvalStatus = tc.approval_status || 'pending';
            return (
              <div key={idx} className={`border-b border-slate-100 last:border-b-0 ${
                approvalStatus === 'approved' ? 'bg-emerald-50/30' :
                approvalStatus === 'rejected' ? 'bg-red-50/30' : ''
              }`} data-testid={`shared-target-${idx}`}>
                <div className="px-5 py-4">
                  {/* Row 1: Company info + badges */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1.5">
                        <p className="text-sm font-semibold text-slate-900">{tc.company_name}</p>
                        <span className="text-[10px] text-slate-400">{tc.country}</span>
                        {tc.priority_tier && <Badge className={`text-[10px] ${TIER_COLORS[tc.priority_tier] || 'bg-slate-400 text-white'}`}>{tc.priority_tier}</Badge>}
                        {tc.fit_category && <Badge className={`text-[10px] ${FIT_COLORS[tc.fit_category] || 'bg-slate-100 text-slate-600'}`}>{tc.fit_category}</Badge>}
                        {tc.fit_score != null && <span className="text-[10px] text-slate-500 font-mono font-semibold" title="AI-computed strategic fit for this asset (0–100)">{Math.round(tc.fit_score)}/100</span>}
                        {tc.confidence && <span className={`text-[9px] font-semibold ${tc.confidence === 'High' ? 'text-emerald-600' : tc.confidence === 'Medium' ? 'text-amber-600' : 'text-slate-400'}`}>{tc.confidence}</span>}
                        {approvalStatus !== 'pending' && (
                          <Badge className={`text-[10px] ${approvalStatus === 'approved' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`}>
                            {approvalStatus === 'approved' ? 'Approved' : 'Rejected'}
                          </Badge>
                        )}
                      </div>
                      {(tc.buyer_archetype || tc.deal_type) && (
                        <div className="flex items-center gap-2 text-[10px] text-slate-400 mb-2">
                          {tc.buyer_archetype && <span>{tc.buyer_archetype}</span>}
                          {tc.buyer_archetype && tc.deal_type && <span>-</span>}
                          {tc.deal_type && <span className="text-[#00709B]">{tc.deal_type}</span>}
                        </div>
                      )}
                      <p className="text-xs text-slate-600 leading-relaxed">{tc.rationale}</p>
                      {tc.approval_comment && (
                        <div className="mt-2 bg-slate-50 rounded-lg px-3 py-2">
                          <p className="text-[10px] text-slate-500"><MessageSquare className="w-3 h-3 inline mr-1" />Comment: {tc.approval_comment}</p>
                        </div>
                      )}
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Button
                        size="sm"
                        variant={approvalStatus === 'approved' ? 'default' : 'outline'}
                        className={`h-8 text-xs ${approvalStatus === 'approved' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'text-emerald-600 border-emerald-200 hover:bg-emerald-50'}`}
                        onClick={() => handleApproval(tc.company_name, 'approved')}
                        disabled={submitting === tc.company_name}
                        data-testid={`approve-${idx}`}
                      >
                        <Check className="w-3.5 h-3.5 mr-1" />Approve
                      </Button>
                      <Button
                        size="sm"
                        variant={approvalStatus === 'rejected' ? 'default' : 'outline'}
                        className={`h-8 text-xs ${approvalStatus === 'rejected' ? 'bg-red-500 hover:bg-red-600 text-white' : 'text-red-500 border-red-200 hover:bg-red-50'}`}
                        onClick={() => {
                          if (isExpanded) {
                            handleApproval(tc.company_name, 'rejected');
                          } else {
                            setExpandedComment(tc.company_name);
                            setCommentText('');
                          }
                        }}
                        disabled={submitting === tc.company_name}
                        data-testid={`reject-${idx}`}
                      >
                        <X className="w-3.5 h-3.5 mr-1" />Reject
                      </Button>
                      <button
                        className="p-1 text-slate-400 hover:text-slate-600"
                        onClick={() => { setExpandedComment(isExpanded ? null : tc.company_name); setCommentText(''); }}
                      >
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Comment input */}
                  {isExpanded && (
                    <div className="mt-3 flex items-end gap-2">
                      <Textarea
                        value={commentText}
                        onChange={(e) => setCommentText(e.target.value)}
                        placeholder="Add a comment (e.g., 'We already have a relationship with them')..."
                        className="text-xs min-h-[60px] resize-none"
                        data-testid={`comment-input-${idx}`}
                      />
                      <Button size="sm" className="h-8 text-xs bg-red-500 hover:bg-red-600 text-white shrink-0"
                        onClick={() => handleApproval(tc.company_name, 'rejected')}
                        disabled={submitting === tc.company_name}
                      >
                        Reject{commentText ? ' with Comment' : ''}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="text-center mt-8 pb-8">
          <p className="text-[10px] text-slate-400">Powered by ForesightRx — Intelligence Infrastructure for Biopharma Deal-Making</p>
        </div>
      </div>
    </div>
  );
}
